import {
    Camera,
    PerspectiveCamera,
    Plane,
    Vector3
} from 'three';

// Super-minimal physics-based constraint system.
// Borrowed a few ideas from the second half of
//   https://www.toptal.com/game/video-game-physics-part-iii-constrained-rigid-body-simulation
// Basically - as we have a really simple, one-moving body system - we do physics math using
// impulses (rather than forces), to avoid complex equation solving.
//
// The main equation you need to know:
//   impulse = mass * velocity = Force * dt.
//
// It's really overkill for what we're doing, but still helpful to keep it physics-based
// rather than purely ad-hoc position modifications.

class CameraConstraintInterface {
    calcImpulse(prevPosition: Vector3, candidatePosition: Vector3, cameraMass: number, deltat: number, bInteractionInProgress: boolean, userImpulse: Vector3, iteration: number):
        {impulse: Vector3, bRepeat: boolean}
    {
        return null;
    }
    m_name: string;
};


// Keep camera on one side of an infinite plane
// (Or, a finite plane - can use "limits" to define edges of the primary plane)
export class PlaneConstraint extends CameraConstraintInterface {
    constructor(name: string, plane: Plane, finiteEdges: Plane[] = [], springStrength: number = -1, disableDepth: number = -1)
    {
        super();
        this.m_name = name;
        this.m_plane = plane.clone();
        this.m_finiteEdges = [];
        for (let finiteEdge of finiteEdges)
            this.m_finiteEdges.push(finiteEdge.clone());
        this.m_springStrength = springStrength;
        this.m_disableDepth = disableDepth;
    }
    calcImpulse(prevPosition: Vector3, candidatePosition: Vector3, cameraMass: number, deltat: number, bInteractionInProgress: boolean, userImpulse: Vector3, iteration: number):
        {impulse: Vector3, bRepeat: boolean}
    {
        const conDist : number = -this.m_plane.distanceToPoint(candidatePosition);
        const eps : number = 1e-2;
        if (conDist <= eps)
            return null;
        for (let finiteEdge of this.m_finiteEdges) {
            if (finiteEdge.distanceToPoint(candidatePosition) < 0)
                return null;
        }
        if (this.m_disableDepth > 0 && conDist > this.m_disableDepth) {
            const prevConDist : number = -this.m_plane.distanceToPoint(prevPosition);
            // Did we cross from the non-disable to the disable side this timestep?
            if (prevConDist < this.m_disableDepth + eps) {
                //console.log(`Breakthrough ${this.m_name}: conDist=${conDist.toFixed(2)} prevConDist=${prevConDist.toFixed(2)} disableDepth=${this.m_disableDepth}`)
                // prevConDist < 0
                // conDist > disableDepth > 0
                // We want conDist = disableDepth * 0.9
                if (this.m_springStrength > 0) {
                    // We're a spring. We're not going to be strong enough to keep it
                    // in range. Switch to a hard constraint now, but a hard constraint
                    // *to the last position*, not at the spring rest position.
                    const conDistNew = conDist - prevConDist + eps;
                    return {
                        impulse: this.m_plane.normal.clone().multiplyScalar(conDistNew/deltat * cameraMass),
                        bRepeat: true
                    };
                }
            }
            else
                // No, we were already disabled. Just ignore.
                return null;
        }
        if (this.m_springStrength <= 0)
            return {impulse: this.m_plane.normal.clone().multiplyScalar(conDist/deltat * cameraMass), bRepeat: false};
        else {
            // F = -kx for a spring ; impulse = F * deltat;
            let springImpulseMag = conDist * this.m_springStrength * deltat;
            const userImpulseMag = userImpulse.dot(this.m_plane.normal);
            if (bInteractionInProgress) {
                // Negative means user is pulling against spring. We resist this with the spring force,
                // meaning increasing resistance the further the spring is pulled.
                // We *don't* apply the spring force when zero/positive - once user achieves the pull, we treat
                // them as infinitely strong while fingers are down.
                //
                // Once fingers are released (!bInteractionInProgress), *then* we allow the spring to return
                // to resting position.
                springImpulseMag = Math.min(springImpulseMag, -userImpulseMag);
                if (springImpulseMag <= eps)
                    return null;
            }
            return {impulse: this.m_plane.normal.clone().multiplyScalar(springImpulseMag), bRepeat: false};
        }
    }

    private m_plane : Plane;
    private m_finiteEdges: Plane[];
    private m_springStrength : number;  // negative: hard constraint.
    private m_disableDepth: number;
}

export class CameraConstraints {
    constructor(camera: PerspectiveCamera) {
        this.m_camera = camera;
    }
    m_camera: PerspectiveCamera;
    m_constraints: CameraConstraintInterface[] = [];
    private m_mass: number = 1;
    private m_lastPosition: Vector3 = null;
    apply(deltat: number, bInteractionInProgress: boolean, bRestartConstraints: boolean) {
        deltat = Math.max(1/60, deltat);
        // Restart: sometimes our lastposition is really stale, due to a period of no constraint
        // evaluation.
        if (this.m_lastPosition == null || bRestartConstraints)
            this.m_lastPosition = this.m_camera.position.clone();

        const userDeltaPos = this.m_camera.position.clone().sub(this.m_lastPosition);
        const userImpulse = userDeltaPos.clone().multiplyScalar(this.m_mass * 1/deltat);
        let candidatePosition : Vector3 = new Vector3;
        let impulses : Vector3[] = [ userImpulse ];
  
        candidatePosition.copy(this.m_lastPosition);
        for (let impulse of impulses)
            candidatePosition.addScaledVector(impulse, (1 / this.m_mass) * deltat);

        const maxIterations = 5;
        let logMessage = '';
        let constraints = this.m_constraints;
        // Why do we iterate? Right now, only one situation: when a spring constraint has a depth
        // limit. If the user pushes through the spring constraint to a point beyond, where the
        // spring would no longer apply, then we *really* want to make sure we block that.
        //
        // That class of constraint gets a few extra tries via extra iterations; no others get
        // extra tries.
        for (let iteration = 0; iteration < maxIterations && constraints.length > 0; ++iteration) {
            let nextConstraints : CameraConstraintInterface[] = [];
            for (let constraint of constraints) {
                let impulse = constraint.calcImpulse(this.m_lastPosition, candidatePosition, this.m_mass, deltat, bInteractionInProgress, userImpulse, iteration);
                if (impulse != null) {
                    impulses.push(impulse.impulse);
                    if (impulse.bRepeat)
                        nextConstraints.push(constraint);
                    logMessage += constraint.m_name + (impulse.bRepeat ? '* ' : ' ');
                }
            }
            ++iteration;
            constraints = nextConstraints;

            candidatePosition.copy(this.m_lastPosition);
            for (let impulse of impulses)
                candidatePosition.addScaledVector(impulse, (1 / this.m_mass) * deltat);
        }

        if (logMessage.length > 0)
            console.log("impulses = " + logMessage);
        //if (impulses.length > 0)
        //    console.log("impulse mag = " + candidatePosition.distanceTo(this.m_camera.position));
        this.m_camera.position.copy(candidatePosition);
        this.m_lastPosition.copy(this.m_camera.position);
    }
}
